import dayjs from "dayjs";
import { trackEvent } from "./mixpanel";

// Matching Mixpanel timeout: 30 minutes in milliseconds
const PRODUCTION_TIMEOUT = 30 * 60 * 1000;
// Local dev mode timeout: 15 seconds in milliseconds
const DEBUG_TIMEOUT = 15 * 1000;

let sessionStartTime: number | null = null;
let lastActivityTime: number | null = null;
let sessionCount = 0;
let isSessionActive = false;
let sessionTimeoutId: NodeJS.Timeout | null = null;
let usingDebugTimeout = process.env.NODE_ENV === "development";

const getTimeout = () =>
  usingDebugTimeout ? DEBUG_TIMEOUT : PRODUCTION_TIMEOUT;

export const toggleDebugTimeout = () => {
  usingDebugTimeout = !usingDebugTimeout;

  // If a session is active, update the timeout
  if (isSessionActive && lastActivityTime) {
    if (sessionTimeoutId) {
      clearTimeout(sessionTimeoutId);
      sessionTimeoutId = null;
    }
    setupInactivityTimeout();
  }

  return usingDebugTimeout;
};

export const setDebugTimeout = (useDebug: boolean) => {
  if (usingDebugTimeout !== useDebug) {
    usingDebugTimeout = useDebug;

    if (isSessionActive && lastActivityTime) {
      if (sessionTimeoutId) {
        clearTimeout(sessionTimeoutId);
        sessionTimeoutId = null;
      }
      setupInactivityTimeout();
    }
  }

  return usingDebugTimeout;
};

export const isUsingDebugTimeout = () => usingDebugTimeout;

// Save all session state to localStorage
const saveSessionState = () => {
  try {
    if (typeof window !== "undefined") {
      localStorage.setItem("sessionCount", sessionCount.toString());
      localStorage.setItem("isSessionActive", isSessionActive.toString());

      if (sessionStartTime) {
        localStorage.setItem("sessionStartTime", sessionStartTime.toString());
      } else {
        localStorage.removeItem("sessionStartTime");
      }

      if (lastActivityTime) {
        localStorage.setItem("lastActivityTime", lastActivityTime.toString());
      } else {
        localStorage.removeItem("lastActivityTime");
      }
    }
  } catch (e) {
    console.error("Error saving session state:", e);
  }
};

// Initialize session state from localStorage
const initializeSessionState = () => {
  try {
    const storedCount = localStorage.getItem("sessionCount");
    if (storedCount) {
      sessionCount = parseInt(storedCount, 10);
    }

    const storedActive = localStorage.getItem("isSessionActive");
    if (storedActive) {
      isSessionActive = storedActive === "true";
    }

    const storedStartTime = localStorage.getItem("sessionStartTime");
    if (storedStartTime) {
      sessionStartTime = parseInt(storedStartTime, 10);
    }

    const storedLastActivity = localStorage.getItem("lastActivityTime");
    if (storedLastActivity) {
      lastActivityTime = parseInt(storedLastActivity, 10);
    }

    if (isSessionActive && lastActivityTime) {
      const now = dayjs().valueOf();
      const timeSinceLastActivity = now - lastActivityTime;

      if (timeSinceLastActivity > getTimeout()) {
        endSessionTimer(true);
      } else {
        setupInactivityTimeout();
      }
    }
  } catch (e) {
    console.error("Error initializing session state:", e);
  }
};

if (typeof window !== "undefined") {
  initializeSessionState();
}

// Save session count to localStorage
const saveSessionCount = () => {
  try {
    if (typeof window !== "undefined") {
      localStorage.setItem("sessionCount", sessionCount.toString());
    }
  } catch (e) {
    console.error("Error saving session count:", e);
  }
};

export const startSessionTimer = () => {
  const now = dayjs().valueOf();

  // If no active session, start a new one
  if (!isSessionActive) {
    sessionStartTime = now;
    lastActivityTime = now;
    sessionCount++;
    isSessionActive = true;

    saveSessionState();

    const startData = {
      start_time: dayjs(sessionStartTime).toISOString(),
      session_count: sessionCount,
      user_agent:
        typeof window !== "undefined" ? window.navigator.userAgent : "",
      viewport_width: typeof window !== "undefined" ? window.innerWidth : 0,
      viewport_height: typeof window !== "undefined" ? window.innerHeight : 0,
      referrer: typeof document !== "undefined" ? document.referrer : "",
      url: typeof window !== "undefined" ? window.location.href : "",
      timeout_duration_ms: getTimeout(),
      debug_mode: usingDebugTimeout,
    };

    trackEvent("session_started", startData);

    setupInactivityTimeout();
  }

  // Update last activity time
  recordActivity();
};

export const recordActivity = () => {
  // If no session is active, start one
  if (!isSessionActive) {
    startSessionTimer();
    return;
  }

  lastActivityTime = dayjs().valueOf();

  saveSessionState();

  if (sessionTimeoutId) {
    clearTimeout(sessionTimeoutId);
    sessionTimeoutId = null;
  }

  setupInactivityTimeout();

  checkMidnightReset();
};

export const checkMidnightReset = () => {
  if (!lastActivityTime || !sessionStartTime) return;

  const lastActivityDay = dayjs(lastActivityTime).startOf("day");
  const currentDay = dayjs().startOf("day");

  // If we crossed midnight, end current session and start a new one
  if (!lastActivityDay.isSame(currentDay)) {
    // End session with the timestamp of the last activity
    const sessionLength = Math.floor(
      (lastActivityTime - sessionStartTime) / 1000
    );

    if (sessionLength >= 1) {
      const endData = {
        duration_seconds: sessionLength,
        start_time: dayjs(sessionStartTime).toISOString(),
        end_time: dayjs(lastActivityTime).toISOString(),
        session_count: sessionCount,
        end_reason: "midnight_reset",
        url: typeof window !== "undefined" ? window.location.href : "",
        timeout_duration_ms: getTimeout(),
        debug_mode: usingDebugTimeout,
      };

      trackEvent("session_ended", endData);
    }

    // Start a new session
    sessionStartTime = dayjs().valueOf();
    lastActivityTime = sessionStartTime;
    sessionCount++;

    saveSessionState();

    const startData = {
      start_time: dayjs(sessionStartTime).toISOString(),
      session_count: sessionCount,
      user_agent:
        typeof window !== "undefined" ? window.navigator.userAgent : "",
      viewport_width: typeof window !== "undefined" ? window.innerWidth : 0,
      viewport_height: typeof window !== "undefined" ? window.innerHeight : 0,
      referrer: typeof document !== "undefined" ? document.referrer : "",
      url: typeof window !== "undefined" ? window.location.href : "",
      timeout_duration_ms: getTimeout(),
      debug_mode: usingDebugTimeout,
    };
    trackEvent("session_started", startData);

    setupInactivityTimeout();
  }
};

export const setupInactivityTimeout = () => {
  if (!isSessionActive || sessionTimeoutId) return;

  const timeoutDuration = getTimeout();

  // Set a timeout to end the session after inactivity period
  sessionTimeoutId = setTimeout(() => {
    if (!lastActivityTime || !sessionStartTime) return;
    endSessionTimer(true);
  }, timeoutDuration);

  return sessionTimeoutId;
};

// When user leaves the page, don't end the session immediately
// but ensure the timeout is set up
export const pauseSession = () => {
  if (isSessionActive && lastActivityTime) {
    if (!sessionTimeoutId) {
      setupInactivityTimeout();
    }
  }
};

export const endSessionOnPageHide = () => {
  if (!isSessionActive || !sessionStartTime || !lastActivityTime) return;
  if (sessionTimeoutId) {
    clearTimeout(sessionTimeoutId);
    sessionTimeoutId = null;
  }

  const endTime = lastActivityTime;
  const sessionLength = Math.floor((endTime - sessionStartTime) / 1000);

  if (sessionLength < 1) {
    isSessionActive = false;
    sessionStartTime = null;
    lastActivityTime = null;
    saveSessionState();
    return;
  }

  const MAX_SESSION_SECONDS = 4 * 60 * 60; // 4 hours
  const finalLength = Math.min(sessionLength, MAX_SESSION_SECONDS);

  const endData = {
    duration_seconds: finalLength,
    start_time: dayjs(sessionStartTime).toISOString(),
    end_time: dayjs(endTime).toISOString(),
    session_count: sessionCount,
    end_reason: "pagehide",
    url: typeof window !== "undefined" ? window.location.href : "",
    timeout_duration_ms: getTimeout(),
    debug_mode: usingDebugTimeout,
  };

  trackEvent("session_ended", endData);

  isSessionActive = false;
  sessionStartTime = null;
  lastActivityTime = null;
  saveSessionState();
};

export const endSessionTimer = (timeout: boolean = false) => {
  if (!isSessionActive || !sessionStartTime || !lastActivityTime) return;

  if (sessionTimeoutId) {
    clearTimeout(sessionTimeoutId);
    sessionTimeoutId = null;
  }

  // Use the timestamp of the last activity if it's a timeout
  const endTime = timeout ? lastActivityTime : dayjs().valueOf();
  const sessionLength = Math.floor((endTime - sessionStartTime) / 1000);

  if (sessionLength < 1) {
    isSessionActive = false;
    sessionStartTime = null;
    lastActivityTime = null;
    saveSessionState();
    return;
  }

  const endData = {
    duration_seconds: sessionLength,
    start_time: dayjs(sessionStartTime).toISOString(),
    end_time: dayjs(endTime).toISOString(),
    session_count: sessionCount,
    end_reason: timeout ? "timeout" : "user_action",
    url: typeof window !== "undefined" ? window.location.href : "",
    timeout_duration_ms: getTimeout(),
    debug_mode: usingDebugTimeout,
  };

  trackEvent("session_ended", endData);

  isSessionActive = false;
  sessionStartTime = null;
  lastActivityTime = null;

  saveSessionState();
};

export const getSessionStats = () => {
  const now = dayjs().valueOf();

  if (!sessionStartTime || !lastActivityTime) {
    return {
      session_active: false,
      session_count: sessionCount,
      timeout_duration: getTimeout() / 1000,
      timeout_mode: usingDebugTimeout ? "DEBUG (15s)" : "PRODUCTION (30m)",
    };
  }

  const timeSinceLastActivity = now - lastActivityTime;

  const timeoutDuration = getTimeout();
  const timeoutRemaining = timeoutDuration - timeSinceLastActivity;

  return {
    session_count: sessionCount,
    session_start: dayjs(sessionStartTime).format("HH:mm:ss"),
    last_activity: dayjs(lastActivityTime).format("HH:mm:ss"),
    current_duration: Math.floor((now - sessionStartTime) / 1000),
    inactivity_seconds: Math.floor(timeSinceLastActivity / 1000),
    is_active: isSessionActive,
    timeout_in: Math.max(0, Math.floor(timeoutRemaining / 1000)),
    timeout_id: sessionTimeoutId ? "Set" : "Not Set",
    timeout_duration: timeoutDuration / 1000,
    timeout_mode: usingDebugTimeout ? "DEBUG (15s)" : "PRODUCTION (30m)",
  };
};

// Local dev - force a session timeout
export const _debugForceTimeout = () => {
  if (isSessionActive && lastActivityTime) {
    endSessionTimer(true);
  } else {
    console.log("No active session to timeout");
  }
};

// Local dev - force a midnight crossing
export const _debugForceMidnightCrossing = () => {
  if (isSessionActive && lastActivityTime) {
    console.log("Forcing midnight crossing...");
    const yesterdayLastActivity = dayjs().subtract(1, "day").valueOf();
    lastActivityTime = yesterdayLastActivity;
    checkMidnightReset();
  } else {
    console.log("No active session for midnight crossing");
  }
};
